import React, { useEffect, useState } from "react";
import Request from "../../../services/Request";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../../../tailwindComponents/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../../../tailwindComponents/ui/tooltip";
import { TrendingDown, TrendingUp, Info, AlertCircle } from "lucide-react";
import ReportService from "../../../services/reports/Report.service";
import CurrentClientProvider from "../../../providers/CurrentClientProvider";
import { ProgressDashboardVizChartData, ProgressDashboardVizRawData, ProgressDashboardVizResponse } from "../../../interfaces/reports/ProgressDashboardReport.interface";
import { QuickInfoCard } from "../../dashboard/QuickInfoCards";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import BasicTable from "../../../tailwindComponents/tables/basic-table.component";

// Get today's date in MMM yy using moment
import moment from "moment";
const TODAY_MMM_YY = moment().format("MMM YY");

const infoBox = (
	<div className="w-full flex items-center border border-purple-200 bg-purple-50 rounded p-4 mb-2">
		<AlertCircle className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0" />
		<div className="text-sm text-purple-900 text-left">
			<span className="font-semibold">Welcome to your report!</span> This report tracks your month-to-date sales
			progress against your plan, providing data-driven insights to boost profitability.
		</div>
	</div>
);

// Helper to aggregate for summary
function getSummaryFromData(data: ProgressDashboardVizRawData[]) {
	const trendSales = data.reduce((sum, row) => sum + (row.trendSales || 0), 0);
	const totalPlan = data.reduce((sum, row) => sum + row.salesPlan, 0);
	const totalLastYear = data.reduce((sum, row) => sum + row.salesLastYear, 0);
	const progressToGoal = totalPlan ? ((trendSales - totalPlan) / totalPlan) * 100 : 0;
	const growthVsLastYear = totalLastYear ? ((trendSales - totalLastYear) / totalLastYear) * 100 : 0;
	return {
		trendSales,
		progressToGoal,
		growthVsLastYear,
		totalLastYear,
	};
}

const handleGetData = async (clientId: string | number, epochMonth: number | null) => {
    try {
        const response = await Request.get(`/api/clients/${clientId}/reports/progress-dashboard/viz`, {
            clientId,
            epochMonth,
        });
        return (response.data || response) as ProgressDashboardVizResponse;
    } catch (error) {
        console.error("Error fetching progress dashboard data:", error);
        return [];
    }
};

interface LocationItem {
	code: string;
	name: string;
}

const ProgressDashboardViz = ({ clientId, epochMonth }: { clientId?: string | number; epochMonth?: number | null }) => {
	const currentClient = CurrentClientProvider.useClient() as any;
	const trendRef = React.useRef<number | null>(null);
	const [rawData, setRawData] = useState<ProgressDashboardVizRawData[]>([]);
	const [chartData, setChartData] = useState<ProgressDashboardVizChartData[]>([]);
	const [locations, setLocations] = useState<LocationItem[]>([]);
	const [selectedLocation, setSelectedLocation] = useState<string>("all-locations");
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!currentClient || !currentClient.client?.Id) return;
		setLoading(true);

        handleGetData(clientId || currentClient.client.Id, epochMonth || null).then((data: any) => {
			const chartData = data?.chartData || [];
			setChartData(chartData.length ? chartData : []);
			data = data?.rawData || data || [];
            const rows = data?.data || data || [] as ProgressDashboardVizRawData[];;
            setRawData(rows.length ? rows : []);
            // Build a map of locationCode -> locationName from the data
            const locMap = new Map<string, string>();
            rows.forEach((r: any) => {
                if (r.locationCode && r.locationName) {
                    locMap.set(r.locationCode, r.locationName);
                } else if (r.locationCode) {
                    locMap.set(r.locationCode, r.locationCode);
                }
            });
            const uniqueLocs = Array.from(locMap.keys()).sort();
            setLocations(uniqueLocs.map(code => ({
                code,
                name: locMap.get(code) || code
            })));
            setSelectedLocation("all-locations");
            setLoading(false);
        });

	}, [currentClient, epochMonth]);

	const filteredData = rawData.filter((row) =>
		selectedLocation === "all-locations" || row.locationCode === selectedLocation
	);

	// Aggregate by classCode
	const classMap: Record<string, { className: string; sales: number; trendSales: number; goal: number; lastYear: number }> = {};
	filteredData.forEach((row) => {
		if (!classMap[row.classCode]) {
			classMap[row.classCode] = {
				className: row.className,
				sales: 0,
				trendSales: 0,
				goal: 0,
				lastYear: 0,
				// monthName: row.monthName || row.month || "",
			};
		}
		classMap[row.classCode].sales += row.sales || 0;
		classMap[row.classCode].goal += row.salesPlan || 0;
		classMap[row.classCode].lastYear += row.salesLastYear || 0;
		classMap[row.classCode].trendSales += row.trendSales || 0;
	});

	const classData = Object.entries(classMap).map(([classCode, vals]) => {
		const perf = vals.goal ? ((vals.trendSales - vals.goal) / vals.goal) * 100 : 0;
		const lastYear = vals.lastYear ? ((vals.trendSales - vals.lastYear) / vals.lastYear) * 100 : 0;
		return {
			classCode,
			className: vals.className,
			sales: vals.sales,
			trendSales: vals.trendSales,
			goal: vals.goal,
			perf: perf >= 0 ? `+${perf.toFixed(0)}%` : `${perf.toFixed(0)}%`,
			lastYear: lastYear >= 0 ? `+${lastYear.toFixed(0)}%` : `${lastYear.toFixed(0)}%`,
		};
	});

	// Summary
	const summary = getSummaryFromData(filteredData);
	const summaryData = [
		{
			title: "Trended Sales This Month",
			value: summary.trendSales,
			description: "Projected month end total based on current projection",
			icon: "DollarSign" as const,
			color: "black",
			tooltipText: "Projected month end total based on current projection."
		},
		{
			title: "Projected Growth vs. Last Year",
			value: summary.growthVsLastYear,
			description: `Trending ${ReportService.formatCurrency(summary.trendSales)} vs ${ReportService.formatCurrency(summary.totalLastYear)} last year`,
			icon: summary.growthVsLastYear >= 0 ? "TrendingUp" as const : "TrendingDown" as const,
			color: summary.growthVsLastYear >= 0 ? "green-500" : "red-500",
			tooltipText: "Growth compared to the same period last year. Positive is up, negative is down."
		},
	];

	// Top performers and needs attention
	const topPerformers = classData
		.filter((row) => parseFloat(row.perf) > 0)
		.sort((a, b) => parseFloat(b.perf) - parseFloat(a.perf))
		.slice(0, 2)
		.map((row) => ({ name: row.className, stat: row.perf }));
	const needsAttention = classData
		.filter((row) => parseFloat(row.perf) < 0)
		.sort((a, b) => parseFloat(a.perf) - parseFloat(b.perf))
		.slice(0, 2)
		.map((row) => ({ name: row.className, stat: row.perf }));

	// Prepare chart data from chartData state (not filteredData)
    const months = chartData.map(row => row.monthName);
    const trendSalesSeries = chartData.map(row => row.monthName == TODAY_MMM_YY ? Math.round(row.trendSales / (row.trend ?? 1 / 31)) : Math.round(row.trendSales));
    const goalSeries = chartData.map(row => Math.round(row.planSales));
    const lastYearSeries = chartData.map(row => Math.round(row.lastYearSales));

    const chartOptions = {
        chart: { type: "line", height: 320 },
        title: { text: "Monthly Sales Performance Trend", style: { fontSize: "20px", color: "#6D28D9" } },
        subtitle: { text: "Monthly trending performance compared to goals and last year's results." },
        xAxis: { categories: months, title: { text: null } },
        yAxis: {
			title: { text: null },
			labels: {
				formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
					return `$${Highcharts.numberFormat(this.value as number, 0)}`;
				}
			}
		},
        legend: { align: "center", verticalAlign: "bottom" },
        series: [
            {
                name: "Trending Sales",
                data: trendSalesSeries,
                color: "#14b8a6",
                marker: { symbol: "circle" }
            },
            {
                name: "Goal (Forecast)",
                data: goalSeries,
                color: "#f43f5e",
                dashStyle: "ShortDash"
            },
            {
                name: "Last Year Actual",
                data: lastYearSeries,
                color: "#334155",
                dashStyle: "Dot"
            }
        ],
        credits: { enabled: false }
    };

	if (loading) {
		return <div className="p-8 text-center">Loading...</div>;
	}

	return (
		<TooltipProvider>
			<div className="w-full max-w-5xl mx-auto flex flex-col gap-8 p-4">
				<h2 className="text-2xl font-bold mb-2">Progress Report</h2>
				{/* Subheader with client info */}
				<div className="flex items-center mb-4 text-lg font-semibold">
					<span>{currentClient?.client?.ClientCode || "N/A"} | {currentClient?.client?.Name || "N/A"}</span>
				</div>
				{infoBox}
				{/* Location Single Select */}
				<div className="mb-4 justify-end flex align-middle">
					<label className="font-semibold mr-2 mt-2">Filter by Location:</label>
					<select
						id="selectLocation"
						value={selectedLocation}
						onChange={(e) => setSelectedLocation(e.target.value)}
						className="border border-gray-300 rounded p-2 min-w-[200px]"
					>
						<option value="all-locations">All Locations</option>
						{locations.map((loc) => (
							<option key={loc.code} value={loc.code}>
								{loc.name} ({loc.code})
							</option>
						))}
					</select>
				</div>
				{/* Summary Cards */}
				<div className="mb-2">
					<h3 className="text-lg font-semibold text-purple-900 mb-2">
						Overall Performance Summary
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<QuickInfoCard
							title={summaryData[0].title}
							value={summaryData[0].value}
							tooltipText={summaryData[0].tooltipText}
							description={<span className="text-muted-foreground text-xs">{summaryData[0].description}</span>}
							color={summaryData[0].color}
							icon={summaryData[0].icon}
						/>
						<QuickInfoCard
							title={summaryData[1].title}
							value={summaryData[1].value}
							tooltipText={summaryData[1].tooltipText}
							description={<span className="text-muted-foreground text-xs">{summaryData[1].description}</span>}
							color={summaryData[1].color}
							icon={summaryData[1].icon}
                            formatType="percent"
						/>
					</div>
				</div>

				{/* Key Insights & Actions */}
				<Card className="border-orange-200 p-1">
					<CardHeader className="flex flex-row items-center gap-2 pb-1">
						<span className="inline-flex items-center justify-center h-6 w-6 rounded bg-orange-100 mr-2">
							<svg
								className="h-4 w-4 text-orange-600"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
							>
								<circle cx="12" cy="12" r="10" />
								<path d="M12 8v4M12 16h.01" />
							</svg>
						</span>
						<CardTitle className="text-2xl">Key Insights & Actions</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-sm text-gray-900 mb-4">
							Here's what the data is telling you and what you can do next.
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Card className="bg-green-50 border-green-200 p-1">
								<CardHeader className="flex flex-row items-center gap-2 pb-1">
									<TrendingUp className="h-5 w-5 text-green-600" />
									<CardTitle className="text-xl">Top Performers</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="text-green-800 text-xs mb-2">
										These categories are exceeding expectation. Double down on what's working.
									</div>
									<div className="text-black text-sm ml-0">
										{topPerformers.map((item, idx) => {
											const isPositive = item.stat.startsWith("+");
											return (
												<div key={idx} className="mb-1">
													<span className="font-bold">{item.name}:</span>{" "}
													<span className={`font-bold ${isPositive ? "text-green-600" : "text-red-600"}`}>{item.stat}</span>
                                                    <span className="font-bold">{" "}vs Goal</span>
												</div>
											);
										})}
									</div>
								</CardContent>
							</Card>
							<Card className="bg-red-50 border-red-200 p-1">
								<CardHeader className="flex flex-row items-center gap-2 pb-1">
									<TrendingDown className="h-5 w-5 text-red-600" />
									<CardTitle className="text-xl">Needs Attention</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="text-red-800 text-xs mb-2">
										These categories are falling behind. Investigate potential issues.
									</div>
									<div className="text-red-800 text-sm ml-0">
										{needsAttention.map((item, idx) => {
											const isPositive = item.stat.startsWith("+");
											return (
												<div key={idx} className="mb-1">
													<span className="font-bold">{item.name}:</span>{" "}
													<span className={`font-bold ${isPositive ? "text-green-600" : "text-red-600"}`}>{item.stat}</span>
                                                    <span className="font-bold">vs Goal</span>
												</div>
											);
										})}
									</div>
								</CardContent>
							</Card>
						</div>
					</CardContent>
				</Card>

				{/* Monthly Sales Performance Trend Chart */}
				<Card>
					<CardHeader>
						<CardTitle className="text-purple-900 text-xl mb-1">Monthly Sales Performance Trend</CardTitle>
						<CardDescription>
							Monthly trending performance compared to goals and last year's results.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<HighchartsReact highcharts={Highcharts} options={chartOptions} />
					</CardContent>
				</Card>

				{/* Detailed Performance Table */}
				<Card>
					<CardHeader className="flex flex-row items-center gap-2 pb-2">
						<BarChartHeader />
						<CardTitle className="text-2xl">Detailed Performance by Class</CardTitle>
						<Tooltip>
							<TooltipTrigger asChild>
								<Info className="h-4 w-4 text-muted-foreground cursor-help ml-2" />
							</TooltipTrigger>
							<TooltipContent>
								<span>Compare actual sales to your forecast for each class.</span>
							</TooltipContent>
						</Tooltip>
					</CardHeader>
					<CardContent>
						<div className="text-sm text-muted-foreground mb-2">
							Compare actual sales to your forecast for each class.
						</div>
						<div className="max-h-[600px]">
							<BasicTable
								columns={[
									{ name: "Class", sortable: true, customClassName: "px-4 py-2 border-b border-gray-200 bg-white text-left align-middle" },
									{ name: "Sales (MTD)", sortable: true, customClassName: "px-4 py-2 border-b border-gray-200 bg-white text-left align-middle" },
									{ name: "Projected Sales", sortable: true, customClassName: "px-4 py-2 border-b border-gray-200 bg-white text-left align-middle" },
									{ name: "Goal (Forecast)", sortable: true, customClassName: "px-4 py-2 border-b border-gray-200 bg-white text-left align-middle" },
									{ name: "Projected Growth vs Last Year", sortable: true, customFormatting: GrowthCell, customClassName: "px-4 py-2 border-b border-gray-200 bg-white text-left align-middle" }
								]}
								data={classData.map(row => [
									`${row.className} (${row.classCode})`,
									ReportService.formatCurrency(row.sales),
									ReportService.formatCurrency(row.trendSales),
									ReportService.formatCurrency(row.goal),
									parseFloat(row.lastYear)
								])}
								rowClassName={(rowIdx, sortedData) => {
									const growth = sortedData && sortedData[rowIdx] ? sortedData[rowIdx][4] : 0;
									if (typeof growth === "string") {
										const num = parseFloat(growth);
										if (num >= 25) return "bg-green-50";
										if (num <= -25) return "bg-red-50";
									}
									if (typeof growth === "number") {
										if (growth >= 25) return "bg-green-50";
										if (growth <= -25) return "bg-red-50";
									}
									return "";
								}}
								defaultSortColumn={"Projected Growth vs Last Year"}
								defaultSortDirection="desc"
								headerClassName="px-4 py-2 border-b border-gray-200 bg-white text-left font-semibold text-gray-700"
								stickyHeader={true}
								parentDivClassName="max-h-[600px] overflow-y-auto"
							/>
						</div>
					</CardContent>
				</Card>
			</div>
		</TooltipProvider>
	);
};

function BarChartHeader() {
	return (
		<span className="inline-flex items-center justify-center h-6 w-6 rounded bg-purple-100 mr-2">
			<svg
				className="h-4 w-4 text-purple-700"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				viewBox="0 0 24 24"
			>
				<rect x="3" y="12" width="4" height="8" rx="1" />
				<rect x="9" y="8" width="4" height="12" rx="1" />
				<rect x="15" y="4" width="4" height="16" rx="1" />
			</svg>
		</span>
	);
}

// Custom formatting for +/- percent and row backgrounds
function GrowthCell(value: string | number) {
    let num = typeof value === "string" ? parseFloat(value) : value;
    const isPositive = num > 0;
    const color = isPositive ? "text-green-600" : "text-red-600";
    return (
        <span className={`font-bold ${color}`}>
            {num > 0 ? "+" : ""}
            {typeof value === "string" ? value : `${num.toFixed(0)}%`}
        </span>
    );
}

export default ProgressDashboardViz;
